/**
 * ============================================================================
 * ORDERS STORE ACTIONS
 * Refactored using generic Apollo helpers and orders-specific logic
 * ============================================================================
 */

import * as ACTION_TYPES from './action-types'
import * as MUTATIONS from './mutation-types'

// Generic Apollo helpers
import {
	executeSimpleQuery,
	executeMutation,
	executeQueryWithLoading
} from '@/store/helpers/apollo'

// Orders-specific helpers
import {
	buildColumnsFromTable,
	buildOrdersPayload,
	buildProductsPayload,
	buildReturningOrdersPayload,
	buildSupplierOrdersPayload,
	buildSearchSuggestionsPayload
} from '@/store/helpers/ordersOem'

// GraphQL imports - Queries
import FetchOrderNew from './Schema/Queries/FetchOrderNew.gql'
import FetchOrderProducts from './Schema/Queries/FetchOrderProducts.gql'
import FetchOrderProductList from './Schema/Queries/FetchOrderProductList.gql'
import FetchOrders from './Schema/Queries/FetchOrdersNew.gql'
import FetchOrdersMeta from './Schema/Queries/FetchOrdersMetaNew.gql'
import FetchOrdersProducts from './Schema/Queries/FetchOrdersProductsNew.gql'
import FetchOrdersProductsMeta from './Schema/Queries/FetchOrdersProductsMeta.gql'
import FetchOrderSearchSuggestionsNew from './Schema/Queries/FetchOrderSearchSuggestionsNew.gql'
import FetchReservationNew from './Schema/Queries/FetchReservation.gql'
import FetchReservationProducts from './Schema/Queries/FetchReservationProducts.gql'
import FetchReservations from './Schema/Queries/FetchReservations.gql'
import FetchReservationsMeta from './Schema/Queries/FetchReservationsMeta.gql'
import FetchReturningOrder from './Schema/Queries/FetchReturningOrder.gql'
import FetchReturningOrders from './Schema/Queries/FetchReturningOrders.gql'
import FetchReturningOrdersMeta from './Schema/Queries/FetchReturningOrdersMeta.gql'
import FetchReturningOrderProducts from './Schema/Queries/FetchReturningOrderProducts.gql'
import FetchReturningOrderProductList from './Schema/Queries/FetchReturningOrderProductList.gql'
import FetchReturningOrdersProducts from './Schema/Queries/FetchReturningOrdersProducts.gql'
import FetchReturningOrdersProductsMeta from './Schema/Queries/FetchReturningOrdersProductsMeta.gql'
import FetchSupplierOrders from './Schema/Queries/FetchSupplierOrders.gql'
import FetchSupplierOrdersMeta from './Schema/Queries/FetchSupplierOrdersMeta.gql'
import FetchSupplierOrderProducts from './Schema/Queries/FetchSupplierOrderProducts.gql'
import FetchSupplierOrderPresignedUrl from './Schema/Queries/FetchSupplierOrderPresignedUrl.gql'
import FetchCarts from './Schema/Queries/FetchCarts.gql'
import FetchCartsMeta from './Schema/Queries/FetchCartsMeta.gql'

// GraphQL imports - Mutations
import CreateOrderNew from './Schema/Mutations/CreateOrderNew.gql'
import UpdateOrderNew from './Schema/Mutations/UpdateOrderNew.gql'
import EditOrderProduct from './Schema/Mutations/EditOrderProduct.gql'
import RemoveOrderProduct from './Schema/Mutations/RemoveOrderProduct.gql'
import CreateReturningOrder from './Schema/Mutations/CreateReturningOrder.gql'
import UpdateReturningOrder from './Schema/Mutations/UpdateReturningOrder.gql'
import CreateSupplierOrder from './Schema/Mutations/CreateSupplierOrder.gql'
import RepayOrder from './Schema/Mutations/RepayOrder.gql'
import EditSelectedShipmentCourier from './Schema/Mutations/EditSelectedShipmentCourier.gql'

export default {
	/**
     * ========================================================================
     * ORDERS LISTING
     * ========================================================================
     */

	/**
     * Fetch orders
     */
	async [ACTION_TYPES.FETCH_ORDERS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.Table)
		const queryPayload = buildOrdersPayload(payload, state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'orders',
			query: {
				query: FetchOrders,
				variables: {
					payload: queryPayload,
					page,
					size,
					...columns
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_ORDERS, data.ordersNew)
				return data.ordersNew
			}
		})
	},

	/**
     * Fetch orders meta
     */
	async [ACTION_TYPES.FETCH_ORDERS_META] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildOrdersPayload(payload, state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'ordersMeta',
			query: {
				query: FetchOrdersMeta,
				variables: {
					payload: queryPayload,
					page,
					size
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_ORDERS_META, data.ordersMetaNew)
				return data.ordersMetaNew
			}
		})
	},

	/**
     * ========================================================================
     * SINGLE ORDER
     * ========================================================================
     */

	/**
     * Fetch single order
     */
	async [ACTION_TYPES.FETCH_ORDER] ({ commit, rootState }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'order',
			query: {
				query: FetchOrderNew,
				variables: {
					id,
					isAdmin: !!rootState.Auth.user.admin
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_ORDER, data.orderNew)
				return data.orderNew
			}
		})
	},

	/**
     * Create order
     */
	async [ACTION_TYPES.CREATE_ORDER] ({ dispatch, rootState }, payload) {
		const result = await executeMutation({
			mutation: {
				mutation: CreateOrderNew,
				variables: { payload }
			},
			onSuccess: (data) => data.createOrderNew
		})

		// Ensure the lazy Users module is registered before the cross-module dispatch
		await import('@/store/modules/Users/helpers')
		dispatch('Users/EDIT_USER', {
			id: rootState.Auth.user.id,
			selectedCartId: null
		}, { root: true })

		return result
	},

	/**
     * Edit order
     */
	async [ACTION_TYPES.EDIT_ORDER] ({ commit, state }, payload) {
		const backupOrder = structuredClone(state.order)
		const _payload = { ...payload }

		if (state.order) {
			_payload.departmentId = state.order.departmentId
		}

		// Optimistically update UI
		commit(MUTATIONS.EDIT_ORDER, payload)

		try {
			const result = await executeMutation({
				mutation: {
					mutation: UpdateOrderNew,
					variables: { payload: _payload }
				},
				onSuccess: (data) => {
					commit(MUTATIONS.EDIT_ORDER, data.editOrderNew)
					return data.editOrderNew
				},
				onError: (error) => {
					// Rollback on error
					commit(MUTATIONS.SET_ORDER, backupOrder)
					throw error
				}
			})

			return result
		} catch (error) {
			// Ensure rollback even if error handler didn't catch
			commit(MUTATIONS.SET_ORDER, backupOrder)
			throw error
		}
	},

	/**
     * Edit reservation
     */
	async [ACTION_TYPES.EDIT_RESERVATION] ({ commit, state }, payload) {
		const backupReservation = structuredClone(state.reservation)
		const _payload = { ...payload }

		if (state.reservation) {
			_payload.departmentId = state.reservation.departmentId
		}

		// Optimistically update UI
		commit(MUTATIONS.EDIT_RESERVATION, payload)

		try {
			const result = await executeMutation({
				mutation: {
					mutation: UpdateOrderNew,
					variables: { payload: _payload }
				},
				onSuccess: (data) => {
					commit(MUTATIONS.EDIT_RESERVATION, data.editOrderNew)
					return data.editOrderNew
				},
				onError: (error) => {
					// Rollback on error
					commit(MUTATIONS.SET_RESERVATION, backupReservation)
					throw error
				}
			})

			return result
		} catch (error) {
			// Ensure rollback even if error handler didn't catch
			commit(MUTATIONS.SET_RESERVATION, backupReservation)
			throw error
		}
	},

	/**
     * Repay order
     */
	async [ACTION_TYPES.REPAY_ORDER] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: RepayOrder,
				variables: { payload }
			},
			onSuccess: (data) => data.repayOrder?.status
		})
	},

	/**
     * ========================================================================
     * ORDER PRODUCTS
     * ========================================================================
     */

	/**
     * Fetch order products
     */
	async [ACTION_TYPES.FETCH_ORDER_PRODUCTS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.ProductsTable)
		const queryPayload = buildProductsPayload(payload, 'order')

		const data = await executeSimpleQuery({
			query: FetchOrderProducts,
			variables: {
				payload: queryPayload,
				page,
				size,
				...columns
			},
			fetchPolicy: 'no-cache'
		})

		commit(MUTATIONS.SET_ORDER_PRODUCTS, data.orderProductsNew)
		return data.orderProductsNew
	},

	/**
     * Fetch order product list
     */
	async [ACTION_TYPES.FETCH_ORDER_PRODUCT_LIST] ({ commit, state }, payload) {
		const columns = buildColumnsFromTable(state.OrderProductListTable)

		const data = await executeSimpleQuery({
			query: FetchOrderProductList,
			variables: {
				payload,
				...columns
			}
		})

		// Update nested product children if recarId exists
		if (payload.recarId) {
			const updatedProducts = state.products.map(product => {
				if (product.recarId === payload.recarId) {
					return {
						...product,
						children: data.orderProductListNew
					}
				}
				return product
			})
			commit(MUTATIONS.SET_ORDER_PRODUCTS, updatedProducts)
		}

		return data.orderProductListNew
	},

	/**
     * Edit order product
     */
	async [ACTION_TYPES.EDIT_ORDER_PRODUCT] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: EditOrderProduct,
				variables: { payload }
			},
			onSuccess: (data) => data.updateOrderProductNew
		})
	},

	/**
     * Remove order product
     */
	async [ACTION_TYPES.REMOVE_ORDER_PRODUCT] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: RemoveOrderProduct,
				variables: { payload }
			},
			onSuccess: (data) => data.removeOrderProductNew
		})
	},

	/**
     * ========================================================================
     * ORDERS PRODUCTS
     * ========================================================================
     */

	/**
     * Fetch orders products
     */
	async [ACTION_TYPES.FETCH_ORDERS_PRODUCTS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.OrdersProductsTable)
		const queryPayload = buildProductsPayload(payload, 'order', state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'ordersProducts',
			query: {
				query: FetchOrdersProducts,
				variables: {
					payload: queryPayload,
					page,
					size,
					...columns
				},
				fetchPolicy: 'no-cache'
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_ORDERS_PRODUCTS, data.orderProductsNew)
				return data.orderProductsNew
			}
		})
	},

	/**
     * Fetch orders products meta
     */
	async [ACTION_TYPES.FETCH_ORDERS_PRODUCTS_META] (
		{ commit, _state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildProductsPayload(payload, 'order')

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'ordersProductsMeta',
			query: {
				query: FetchOrdersProductsMeta,
				variables: {
					payload: queryPayload,
					page,
					size
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_ORDERS_PRODUCTS_META, data.orderProductsMetaNew)
				return data.orderProductsMetaNew
			}
		})
	},

	/**
     * ========================================================================
     * RESERVATIONS
     * ========================================================================
     */

	/**
     * Fetch reservations
     */
	async [ACTION_TYPES.FETCH_RESERVATIONS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.ReservationsTable)
		const queryPayload = buildOrdersPayload(payload, state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'reservations',
			query: {
				query: FetchReservations,
				variables: {
					payload: queryPayload,
					page,
					size,
					...columns
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_RESERVATIONS, data.reservations)
				return data.reservations
			}
		})
	},

	/**
     * Fetch reservations meta
     */
	async [ACTION_TYPES.FETCH_RESERVATIONS_META] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildOrdersPayload(payload, state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'reservationsMeta',
			query: {
				query: FetchReservationsMeta,
				variables: {
					payload: queryPayload,
					page,
					size
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_RESERVATIONS_META, data.reservationsMeta)
				return data.reservationsMeta
			}
		})
	},

	/**
     * Fetch single reservation
     */
	async [ACTION_TYPES.FETCH_RESERVATION] ({ commit, rootState }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'reservation',
			query: {
				query: FetchReservationNew,
				variables: {
					id,
					isAdmin: !!rootState.Auth.user.admin
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_RESERVATION, data.reservation)
				return data.reservation
			}
		})
	},

	/**
     * Fetch reservation products
     */
	async [ACTION_TYPES.FETCH_RESERVATION_PRODUCTS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.ReservationProductsTable)
		const queryPayload = buildProductsPayload(payload, 'reservation')

		const data = await executeSimpleQuery({
			query: FetchReservationProducts,
			variables: {
				payload: queryPayload,
				page,
				size,
				...columns
			},
			fetchPolicy: 'no-cache'
		})

		commit(MUTATIONS.SET_RESERVATION_PRODUCTS, data.reservationProductsNew)
		return data.reservationProductsNew
	},

	/**
     * ========================================================================
     * RETURNING ORDERS
     * ========================================================================
     */

	/**
     * Fetch returning orders
     */
	async [ACTION_TYPES.FETCH_RETURNING_ORDERS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.ReturningOrdersTable)
		const queryPayload = buildReturningOrdersPayload(payload, state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'returningOrders',
			query: {
				query: FetchReturningOrders,
				variables: {
					payload: queryPayload,
					page,
					size,
					...columns
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_RETURNING_ORDERS, data.returningOrdersNew)
				return data.returningOrdersNew
			}
		})
	},

	/**
     * Fetch returning orders meta
     */
	async [ACTION_TYPES.FETCH_RETURNING_ORDERS_META] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildReturningOrdersPayload(payload, state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'returningOrdersMeta',
			query: {
				query: FetchReturningOrdersMeta,
				variables: {
					payload: queryPayload,
					page,
					size
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_RETURNING_ORDERS_META, data.returningOrdersMetaNew)
				return data.returningOrdersMetaNew
			}
		})
	},

	/**
     * Fetch single returning order
     */
	async [ACTION_TYPES.FETCH_RETURNING_ORDER] ({ commit, rootState }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'returningOrder',
			query: {
				query: FetchReturningOrder,
				variables: {
					id,
					isAdmin: !!rootState.Auth.user.admin
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_RETURNIG_ORDER, data.returningOrderNew)
				return data.returningOrderNew
			}
		})
	},

	/**
     * Create returning order
     */
	async [ACTION_TYPES.CREATE_RETURNING_ORDER] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: CreateReturningOrder,
				variables: { payload }
			},
			onSuccess: (data) => data.createReturningOrderNew?.[0]
		})
	},

	/**
     * Update returning order
     */
	async [ACTION_TYPES.UPDATE_RETURNING_ORDER] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: UpdateReturningOrder,
				variables: { payload }
			},
			onSuccess: (data) => data.updateReturningOrderNew
		})
	},

	/**
     * ========================================================================
     * RETURNING ORDERS PRODUCTS
     * ========================================================================
     */

	/**
     * Fetch returning order products
     */
	async [ACTION_TYPES.FETCH_RETURNING_ORDER_PRODUCTS] (
		{ commit, _state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const data = await executeSimpleQuery({
			query: FetchReturningOrderProducts,
			variables: {
				payload,
				page,
				size
			},
			fetchPolicy: 'no-cache'
		})

		commit(MUTATIONS.SET_RETURNING_ORDER_PRODUCTS, data.returningOrderProductsNew)
		return data.returningOrderProductsNew
	},

	/**
     * Fetch returning order product list
     */
	async [ACTION_TYPES.FETCH_RETURNING_ORDER_PRODUCT_LIST] (_, payload) {
		const data = await executeSimpleQuery({
			query: FetchReturningOrderProductList,
			variables: { payload }
		})

		return data.returningOrderProductListNew
	},

	/**
     * Fetch returning orders products
     */
	async [ACTION_TYPES.FETCH_RETURNING_ORDERS_PRODUCTS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.ReturningOrdersProductsTable)
		const queryPayload = buildReturningOrdersPayload(payload, state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'returningOrdersProducts',
			query: {
				query: FetchReturningOrdersProducts,
				variables: {
					payload: queryPayload,
					page,
					size,
					...columns
				},
				fetchPolicy: 'no-cache'
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_RETURNING_ORDERS_PRODUCTS, data.returningOrderProductsNew)
				return data.returningOrderProductsNew
			}
		})
	},

	/**
     * Fetch returning orders products meta
     */
	async [ACTION_TYPES.FETCH_RETURNING_ORDERS_PRODUCTS_META] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildReturningOrdersPayload(payload, state.searchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'returningOrdersProductsMeta',
			query: {
				query: FetchReturningOrdersProductsMeta,
				variables: {
					payload: queryPayload,
					page,
					size
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_RETURNING_ORDERS_PRODUCTS_META, data.returningOrderProductsMetaNew)
				return data.returningOrderProductsMetaNew
			}
		})
	},

	/**
     * ========================================================================
     * SUPPLIER ORDERS
     * ========================================================================
     */

	/**
     * Fetch supplier orders
     */
	async [ACTION_TYPES.FETCH_SUPPLIER_ORDERS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildSupplierOrdersPayload(payload, state.supplierOrderSearchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'supplierOrders',
			query: {
				query: FetchSupplierOrders,
				variables: {
					payload: queryPayload,
					page,
					size
				},
				fetchPolicy: 'no-cache'
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_SUPPLIER_ORDERS, data.supplierOrdersNew)
				return data.supplierOrdersNew
			}
		})
	},

	/**
     * Fetch supplier orders meta
     */
	async [ACTION_TYPES.FETCH_SUPPLIER_ORDERS_META] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const queryPayload = buildSupplierOrdersPayload(payload, state.supplierOrderSearchFilters)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'supplierOrdersMeta',
			query: {
				query: FetchSupplierOrdersMeta,
				variables: {
					payload: queryPayload,
					page,
					size
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_SUPPLIER_ORDERS_META, data.supplierOrdersNewMeta)
				return data.supplierOrdersMetaNew
			}
		})
	},

	/**
     * Fetch supplier order products
     */
	async [ACTION_TYPES.FETCH_SUPPLIER_ORDER_PRODUCTS] (
		{ commit, state },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const columns = buildColumnsFromTable(state.SupplierOrderProductsTable)
		const productQueryType = payload.supplierOrderIds ? 'supplier_order' : 'order'
		const queryPayload = buildProductsPayload(payload, productQueryType)

		const data = await executeSimpleQuery({
			query: FetchSupplierOrderProducts,
			variables: {
				payload: queryPayload,
				page,
				size,
				...columns
			},
			fetchPolicy: 'no-cache'
		})

		commit(MUTATIONS.SET_SUPPLIER_ORDER_PRODUCTS, data.orderProductsNew)
		return data.orderProductsNew
	},

	/**
     * Create supplier order
     */
	async [ACTION_TYPES.CREATE_SUPPLIER_ORDER] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: CreateSupplierOrder,
				variables: { payload }
			},
			onSuccess: (data) => data.createSupplierOrder
		})
	},

	/**
     * Fetch supplier order presigned URL
     */
	async [ACTION_TYPES.FETCH_SUPPLIER_ORDER_PRESIGNED_URL] (_, path) {
		const data = await executeSimpleQuery({
			query: FetchSupplierOrderPresignedUrl,
			variables: { path }
		})

		return data.supplierOrderPresignedUrl
	},

	/**
     * ========================================================================
     * CARTS
     * ========================================================================
     */

	/**
     * Fetch carts
     */
	async [ACTION_TYPES.FETCH_CARTS] (
		{ commit },
		{ page = 1, size = 20, ...payload } = {}
	) {
		const data = await executeSimpleQuery({
			query: FetchCarts,
			variables: {
				payload,
				page,
				size
			}
		})

		commit(MUTATIONS.SET_CARTS, data.carts)
		return data.carts
	},

	/**
     * Fetch carts meta
     */
	async [ACTION_TYPES.FETCH_CARTS_META] (
		{ commit },
		{ page = 1, size = 20, ...payload } = {}
	) {
		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'cartsMeta',
			query: {
				query: FetchCartsMeta,
				variables: {
					payload,
					page,
					size
				}
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_CARTS_META, data.cartsMeta)
				return data.cartsMeta
			}
		})
	},

	/**
     * ========================================================================
     * SEARCH & UTILITIES
     * ========================================================================
     */

	/**
     * Fetch order search suggestions
     */
	async [ACTION_TYPES.FETCH_ORDER_SEARCH_SUGGESTIONS] (
		{ rootState },
		payload
	) {
		const queryPayload = buildSearchSuggestionsPayload(payload, rootState)

		const data = await executeSimpleQuery({
			query: FetchOrderSearchSuggestionsNew,
			variables: {
				payload: queryPayload,
				size: 20
			}
		})

		return data.orderSearchSuggestionsNew?.nodes || []
	},

	/**
     * Edit selected courier
     */
	async [ACTION_TYPES.EDIT_SELECTED_COURIER] ({ commit, state }, payload) {
		const backupShipment = { ...state.order.shipment }
		const { _orderId, ...updatedShipment } = payload

		// Optimistically update UI
		commit(MUTATIONS.EDIT_ORDER, {
			...state.order,
			shipment: { ...state.order.shipment, ...updatedShipment }
		})

		try {
			const result = await executeQueryWithLoading({
				commit,
				mutationName: MUTATIONS.SET_LOADING,
				loadingKey: 'editCourier',
				query: {
					mutation: EditSelectedShipmentCourier,
					variables: { payload }
				},
				onSuccess: (data) => data.editSelectedShipmentCourier
			})

			return result
		} catch (error) {
			// Rollback on error
			commit(MUTATIONS.EDIT_ORDER, {
				...state.order,
				shipment: backupShipment
			})
			throw error
		}
	}
}
